import { CalendarIcon } from "@heroicons/react/outline";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiResponse } from "../../api/apiResponse";
import {
  cancelReadingScheduleSlot,
  indexReadingScheduleSlots,
} from "../../api/endpoints/candidate/readingScheduleSlots";
import { indexCandidateSessions } from "../../api/endpoints/candidate/sessions";
import ScheduleSlotModel from "../../api/models/ScheduleSlotModel";
import SessionModel from "../../api/models/SessionModel";
import { makeApiRequest } from "../../helpers";
import { CandidateScheduleService } from "../../services/candidateSessionsService";
import CandidateSessionCard from "../sessions/CandidateSessionCard";
import { useQuery } from "react-query";
import { ApiCommonParams } from "../../api/@types/apiTypes";

type ListeningSessionsProps = {
  openBookSession: () => void;
};

export default function ListeningSessions(props: ListeningSessionsProps) {
  const { openBookSession } = props;
  const { t } = useTranslation("", {
    keyPrefix: "page.candidate.dashboard.homepage",
  });
  // API calls
  const { data: sessions, refetch: refetchSessions } = useQuery(
    ["fetchSessions", indexCandidateSessions],
    () => {
      const today = moment().locale("en").format("YYYY-MM-DD");

      let apiParams: ApiCommonParams = {
        orderBy: "session_number",
        filters: { date: today },
      };
      return indexCandidateSessions(apiParams);
    },
    {
      refetchInterval: 1 * 60 * 1000, // 1 minutes
      select: (response) => {
        if (response.isSuccess) {
          const data: SessionModel[] = SessionModel.fromApiResponse(
            response.data
          );
          return { data };
        }
      },
    }
  );

  const cancelSession = async (sessionInfo: SessionModel) => {
    await makeApiRequest(
      () => cancelReadingScheduleSlot(sessionInfo.slotId!),
      (response: ApiResponse) => {
        refetchScheduleSlots();
        refetchSessions();
      }
    );
  };
  const { data: scheduleSlots, refetch: refetchScheduleSlots } = useQuery(
    ["fetchScheduleSlots", indexReadingScheduleSlots],
    () => {
      return indexReadingScheduleSlots();
    },
    {
      refetchInterval: 1 * 60 * 1000, // 1 minutes
      select: (response) => {
        if (response.isSuccess) {
          const data: ScheduleSlotModel[] = ScheduleSlotModel.fromApiResponse(
            response.data
          );
          return { data };
        }
      },
    }
  );
  const displayedSessions = useMemo(() => {
    if (!!scheduleSlots && !!sessions) {
      const candidateSessionsService = new CandidateScheduleService(
        sessions?.data,
        scheduleSlots?.data
      );
      const activeSessions = candidateSessionsService.generateActiveSessions();
      return activeSessions;
    }
    return [];
  }, [
    scheduleSlots?.data,
    scheduleSlots?.data?.length,
    sessions?.data,
    sessions?.data?.length,
  ]);

  return (
    <div className="flex flex-col  gap-4">
      <div className="w-full flex items-center justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-700">
          {t("listeningSessions")}
        </h3>
        <button
          type="button"
          className="w-fit inline-flex   items-center rounded-md border border-gray-300 bg-white px-4  py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
          onClick={() => {
            openBookSession();
          }}
        >
          <CalendarIcon
            className="md:ltr:mr-2 md:ltr:-ml-0.5 md:rtl:ml-2 md:rtl:-mr-0.5 h-5 w-5 text-gray-500"
            aria-hidden="true"
          />
          <span className="hidden md:flex"> {t("bookSession")}</span>
        </button>
      </div>
      <p className="text-base font-normal text-gray-400">
        {t("listeningSessionsDesc")}
      </p>
      {displayedSessions.length > 0 && (
        <div className="w-full">
          <ul className="flex flex-col gap-4" data-cy="activeSessionsList">
            {displayedSessions.map((session, index) => (
              <CandidateSessionCard
                key={`session-${session.id}`}
                sessionNumber={1 + index}
                session={session}
                sessionRanking={index === 0}
                cancelSession={() => cancelSession(session)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
